import { useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import {
	useGetSenderCandidatesQuery,
	useGetSendersUsersQuery,
} from "@/pages/Private/redux/admin/admin.api";
import { User } from "@/pages/Public/pages/Login/schema/login";
import {
	findMeAmongSenders,
	pickDefaultMailbox,
	resolveMyDefaultSender,
} from "@/pages/Private/pages/List/components/CampaignOverview/utils/resolveMyDefaultSender.util";

import { Channel } from "./ChannelSelect";
import { buildSenderOptions, hasMailbox, selectableSenders } from "./senderOptions";

interface UseSenderChannelSelectionParams {
	// Initial sender/channel to seed the dropdowns (active draft's "from") so opening
	// doesn't auto-fire a channel switch that rebuilds the draft list and scrolls the chat.
	initialSenderUserId?: number;
	initialChannel?: Channel;
	prospectHasEmail?: boolean;
	prospectHasLinkedinUrl?: boolean;
	/** The lead's LinkedIn connection check; the default waits for it (PREC-4312, E1). */
	connectedUserIds?: Set<number>;
	connectionCheckDone?: boolean;
	/** The lead (prospect id): a new lead is a new decision, not the last lead's pick (PREC-4312). */
	leadKey?: number;
	onSenderChannelChange?: (params: { userId: number; channel: Channel; emailId?: number }) => void;
}

/**
 * Owns the GenerateBar's sender + channel selection: the sender picker's options (every
 * colleague, enabled when they can reach this lead, see {@link buildSenderOptions}),
 * auto-selection of the default sender/channel, channel availability for the selected
 * sender, and notifying the parent chat editor whenever the (sender, channel) pair changes.
 *
 * The default agrees with the composer's (PREC-4312): the draft's own sender when it has one,
 * otherwise the shared "me or nothing" rule ({@link resolveMyDefaultSender}) — never a colleague,
 * and nothing while the connection check that decides between LinkedIn and email is running.
 * Extracted from {@link GenerateBar} to keep that file under the length gate.
 */
export const useSenderChannelSelection = ({
	initialSenderUserId,
	initialChannel,
	prospectHasEmail,
	prospectHasLinkedinUrl,
	connectedUserIds,
	connectionCheckDone = true,
	leadKey,
	onSenderChannelChange,
}: UseSenderChannelSelectionParams) => {
	const { user } = useAppSelector(authSelector);
	const { data: sendingUsers } = useGetSendersUsersQuery();
	const { data: senderCandidates } = useGetSenderCandidatesQuery();

	const [selectedSendingUser, setSelectedSendingUser] = useState<User | null>(null);
	const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);

	// ── Batch senders: a batch runs over many leads, so it keeps the senders with a connected
	// LinkedIn, as it always has. ──
	const availableUsers = useMemo(() => {
		if (!sendingUsers?.data) {
			return [];
		}

		return sendingUsers.data.filter((u) => !!u.unipile?.isLoggedIn);
	}, [sendingUsers?.data]);

	// ── The single-lead picker lists every colleague and enables those who can reach this lead:
	// an email-only sender too, for a lead with an email address (PREC-4244). ──
	const senderOptions = useMemo(
		() =>
			buildSenderOptions(sendingUsers?.data ?? [], senderCandidates?.data, {
				prospectHasEmail,
				prospectHasLinkedinUrl,
			}),
		[sendingUsers?.data, senderCandidates?.data, prospectHasEmail, prospectHasLinkedinUrl]
	);

	const selectableUsers = useMemo(() => selectableSenders(senderOptions), [senderOptions]);

	// The draft's sender seeds the selection only when it is a real one: a "Select sender"
	// placeholder carries userId 0 and a nominal email channel.
	const seededChannel = initialSenderUserId ? initialChannel : undefined;

	// What the shared rule makes of me for this lead (PREC-4312).
	const myDefault = useMemo(
		() =>
			resolveMyDefaultSender({
				me: findMeAmongSenders(sendingUsers?.data, user?.id),
				prospectHasLinkedinUrl: !!prospectHasLinkedinUrl,
				prospectHasEmail: !!prospectHasEmail,
				connectionCheckDone,
				connectedUserIds: connectedUserIds ?? new Set<number>(),
			}),
		[
			sendingUsers?.data,
			user?.id,
			prospectHasLinkedinUrl,
			prospectHasEmail,
			connectionCheckDone,
			connectedUserIds,
		]
	);

	// Auto-select sender: keep current if still available, otherwise the caller-provided
	// initial sender (the active draft's, so opening the panel doesn't switch the channel),
	// otherwise me when the shared rule gives me a default — never a colleague (PREC-4312).
	// The lead the current selection was made for: on a new lead the last pick is not kept.
	const senderLeadRef = useRef(leadKey);
	const channelLeadRef = useRef(leadKey);

	useEffect(() => {
		const sameLead = senderLeadRef.current === leadKey;

		senderLeadRef.current = leadKey;
		setSelectedSendingUser((prev) => {
			if (sameLead && prev && selectableUsers.some((u) => u.id === prev.id)) {
				return prev;
			}

			const initialSender = initialSenderUserId
				? selectableUsers.find((u) => u.id === initialSenderUserId)
				: undefined;

			if (initialSender) {
				return initialSender;
			}

			if (myDefault.kind !== "email" && myDefault.kind !== "linkedin") {
				return null;
			}

			return selectableUsers.find((u) => u.id === myDefault.userId) ?? null;
		});
	}, [selectableUsers, initialSenderUserId, myDefault, leadKey]);

	// ── Channel availability for the selected sender ──
	const availableChannels = useMemo<Channel[]>(() => {
		if (!selectedSendingUser) {
			return [];
		}

		const channels: Channel[] = [];

		if (prospectHasLinkedinUrl && selectedSendingUser.unipile?.isLoggedIn) {
			channels.push("linkedin");
		}

		if (prospectHasEmail && hasMailbox(selectedSendingUser)) {
			channels.push("email");
		}

		return channels;
	}, [selectedSendingUser, prospectHasEmail, prospectHasLinkedinUrl]);

	// When the sender changes (or its available channels change), make sure the
	// selected channel is still valid; otherwise prefer the caller-provided
	// initial channel (e.g. the active draft's channel so opening the modal
	// doesn't trigger a channel switch), and finally fall back to the first
	// available option.
	useEffect(() => {
		if (availableChannels.length === 0) {
			setSelectedChannel(null);

			return;
		}

		const sameLead = channelLeadRef.current === leadKey;

		channelLeadRef.current = leadKey;
		setSelectedChannel((prev) => {
			if (sameLead && prev && availableChannels.includes(prev)) {
				return prev;
			}

			if (seededChannel && availableChannels.includes(seededChannel)) {
				return seededChannel;
			}

			// LinkedIn or email depends on the connection check: wait for it rather than settle on
			// the first channel, which would stick as `prev` (PREC-4312, E1).
			if (!connectionCheckDone) {
				return null;
			}

			const myChannel = myDefault.kind === "email" || myDefault.kind === "linkedin";

			if (myChannel && selectedSendingUser?.id === myDefault.userId) {
				return myDefault.kind;
			}

			return availableChannels[0];
		});
	}, [
		availableChannels,
		seededChannel,
		myDefault,
		selectedSendingUser?.id,
		connectionCheckDone,
		leadKey,
	]);

	// Keep the latest notify callback in a ref so the sync effect below depends
	// ONLY on the selection — not on the callback identity. The parent's
	// `onSenderChannelChange` is a `useCallback` that depends on `drafts`, so it
	// gets a new identity whenever the active draft changes. If this effect
	// depended on it, editing the draft's "From" in the chat editor would re-fire
	// this effect and clobber the user's pick with GenerateBar's stale selection.
	const onSenderChannelChangeRef = useRef(onSenderChannelChange);

	useEffect(() => {
		onSenderChannelChangeRef.current = onSenderChannelChange;
	}, [onSenderChannelChange]);

	// Notify the parent (chat editor) whenever the (sender, channel) selection
	// changes so the active draft's "From" can be updated in sync. For email the
	// mailbox is the one email rule's (pickDefaultMailbox: primary, else first healthy).
	useEffect(() => {
		const notify = onSenderChannelChangeRef.current;

		if (!notify || !selectedSendingUser || !selectedChannel) {
			return;
		}

		const emailId =
			selectedChannel === "email" ? pickDefaultMailbox(selectedSendingUser.emails)?.id : undefined;

		notify({
			userId: selectedSendingUser.id,
			channel: selectedChannel,
			emailId,
		});
	}, [selectedSendingUser, selectedChannel]);

	return {
		availableUsers,
		senderOptions,
		selectedSendingUser,
		setSelectedSendingUser,
		selectedChannel,
		setSelectedChannel,
		availableChannels,
	};
};
